import type { Contact } from "@/features/contact/types";
import type { Doctor } from "./doctor";
import type { Treatment } from "./treatment";

export const PATIENT_GENDER = {
  MALE: "Male",
  FEMALE: "Female",
} as const;

export type PatientGender =
  (typeof PATIENT_GENDER)[keyof typeof PATIENT_GENDER];

export const APPOINTMENT_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
} as const;

export type AppointmentStatus =
  (typeof APPOINTMENT_STATUS)[keyof typeof APPOINTMENT_STATUS];
export const APPOINTMENT_STATUSES = Object.values(
  APPOINTMENT_STATUS,
) as Array<AppointmentStatus>;

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  pending: "#3b82f6",
  confirmed: "#10b981",
  completed: "#64748b",
  cancelled: "#f43f5e",
  no_show: "#f59e0b",
};

export type AppointmentDoctor = Pick<
  Doctor,
  "id" | "name" | "photo" | "externalId"
>;

export type AppointmentContact = Pick<
  Contact,
  "id" | "firstName" | "lastName" | "phone" | "email" | "imageUrl"
>;

export type AppointmentTreatment = Pick<Treatment, "id" | "name">;

export type AppointmentPatient = {
  firstName: string;
  lastName: string;
  phone: string;
  gender?: PatientGender;
  contactId?: string;
};

export type Appointment = {
  id: string;
  startDate: string;
  endDate: string;
  doctorId: string;
  doctor: AppointmentDoctor;
  contactId: string | null;
  contact?: AppointmentContact | null;
  treatmentId: string | null;
  treatment: AppointmentTreatment | null;
  patients?: AppointmentPatient[];
  status: AppointmentStatus;
  notes: string | null;
  cancellationReason?: string | null;
  color?: string;
  organizationId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  externalId?: string | null;
  externalData?: AppointmentExternalData | null;
  activities?: AppointmentActivity[];
  bookedBy?: AppointmentBookedBy;
  bookedByUserId?: string | null;
  rescheduledCount?: number;
};

export type AppointmentSlot = {
  startDate: Date;
  endDate: Date;
  resourceId?: string;
  bounds?: { x: number; y: number };
};

export type AppointmentViewMode = "calendar" | "list";

export type CreateAppointmentPayload = Pick<
  Appointment,
  | "startDate"
  | "endDate"
  | "contactId"
  | "doctorId"
  | "treatmentId"
  | "patients"
  | "status"
  | "notes"
  | "cancellationReason"
> & {
  integrationId?: string;
  fromSlot?: number;
  toSlot?: number;
};

export type UpdateAppointmentPayload = Partial<CreateAppointmentPayload>;

export type AppointmentFilter = {
  doctorIds?: string[];
  startDate?: string;
  endDate?: string;
  statuses?: AppointmentStatus[];
  contactId?: string;
  bookedByUserIds?: string[];
};

// For react-big-calendar event format
export type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resourceId: string;
  appointment: Appointment;
};

// For react-big-calendar resource format
export type CalendarResource = {
  id: string;
  title: string;
  photo?: string | null;
  type?: "doctor" | "day";
  date?: Date;
};

export type CalendarView = "day" | "week" | "month";

// Activity types
export const APPOINTMENT_ACTIVITY_ACTION = {
  CREATE: "create",
  PATIENT_UPDATE: "patient_update",
  TREATMENT_UPDATE: "treatment_update",
  DOCTOR_UPDATE: "doctor_update",
  STATUS_UPDATE: "status_update",
  SCHEDULE_UPDATE: "schedule_update",
  DELETE: "delete",
  BOOKED_BY_UPDATE: "booked_by_update",
} as const;

export type AppointmentActivityAction =
  (typeof APPOINTMENT_ACTIVITY_ACTION)[keyof typeof APPOINTMENT_ACTIVITY_ACTION];

export const APPOINTMENT_BOOKED_BY = {
  USER: "user",
  CRM: "crm",
  AI: "ai",
} as const;

export type AppointmentBookedBy =
  (typeof APPOINTMENT_BOOKED_BY)[keyof typeof APPOINTMENT_BOOKED_BY];

export type AppointmentActivity = {
  userId: string | null;
  integrationId: string | null;
  action: AppointmentActivityAction;
  timestamp: string;
  payload?: { previous?: unknown; new?: unknown };
};

// CRM (ClinicSoft) types -------------------------------------------------

export type CrmDoctorScheduleSlot = {
  slotId: number;
  availableTime: string;
};

export type CrmDoctorScheduleDay = {
  date: string;
  slots: CrmDoctorScheduleSlot[];
};

export type CrmDoctorScheduleResponse = {
  doctorLicense: string;
  doctorName?: string;
  availability: CrmDoctorScheduleDay[];
};

export type SendAppointmentToCrmPayload = {
  integrationId: string;
  fromSlot: number;
  toSlot: number;
  notes?: string;
};

export type AppointmentExternalRaw = {
  VisitId?: number;
  message?: string;
  [key: string]: unknown;
};

export type AppointmentExternalError = {
  code?: string;
  action?: string;
  details?: string;
  message: string;
  occurredAt: string;
};

export type AppointmentExternalDrift = {
  message: string;
  occurredAt: string;
  orphanedVisitId?: string;
  orphanedEnquiryId?: string;
};

export type AppointmentExternalData = {
  appointmentId?: string;
  patientId?: string;
  enquiryId?: string;
  integrationId?: string;
  fromSlot?: number;
  toSlot?: number;
  fromTime?: string;
  toTime?: string;
  isDrifted?: boolean;
  raw?: AppointmentExternalRaw | null;
  error?: AppointmentExternalError | null;
  drift?: AppointmentExternalDrift | null;
};

export type SendAppointmentToCrmResponse = {
  externalId: string;
  externalData: AppointmentExternalData;
};

export type SyncDoctorsFromCrmResult = {
  fetched: number;
  created: number;
  updated: number;
  skipped: number;
};
